import { useState, useEffect, useMemo } from "react";
import "../Leads.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowBigRightDashIcon, Copy, CopyCheck, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { NOT_ADDED_LEADS as columns, getErrorMessageText } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { formatTime, formatUSPhone, getOwnerColor, getMetricScoreClass, getPhoneTypeLabel, makeFpsLink, statuses, updateLocalStorageRolling } from "../../../utils/common";
import Pagination from "../../../components/Pagination";
import ConfirmModal from "../../../components/ConfirmModal";
import DMReasonConfirmModal from "../../../components/DMReasonConfirmModal";
import axiosInstance from "../../../api/axiosInstance";
import { useAuth } from "../../../context/AuthProvider";
import AddLeadForm from "./AddLeadForm";

const NotAddedLeads = () => {
  const { user } = useAuth();

  const [copiedId, setCopiedId] = useState(null);

  const [fetchLoading, setFetchLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [searchAddress, setSearchAddress] = useState("");

  const [copiedAddresses, setCopiedAddresses] = useState([]);
  const [visitedLinks, setVisitedLinks] = useState([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [leads, setLeads] = useState([]);

  // const indexOfLastLead = currentPage * leadsPerPage;
  // const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  // const totalPages = Math.ceil((totalLeads || 0) / leadsPerPage);

  const permission = user?.permissions?.find((p) => ["not-added-leads", "not-added-leads-l1", "not-added-leads-l2"].includes(p.permission_name) && p.can_view);

  const permissionDelete = user?.permissions?.find((p) => ["not-added-leads", "not-added-leads-l1", "not-added-leads-l2"].includes(p.permission_name) && p.can_delete);

  let leadStatusParam = "";

  if (permission) {
    if (permission?.permission_name === "not-added-leads-l1") {
      leadStatusParam = "&lead_status=0";
    } else if (permission?.permission_name === "not-added-leads-l2") {
      leadStatusParam = "&lead_status=1";
    }
  }

  const filterColumns = useMemo(() => {
    if (permission?.permission_name === "not-added-leads-l2") {
      return columns;
    }

    return columns.filter((col) => col.key !== "reason_move");
  }, [permission]);

  const fetchLeads = async (page = 1, limit = leadsPerPage, showLoader = true) => {
    try {
      if (showLoader) setFetchLoading(true);
      setSearchParams({ page });
      const offset = (page - 1) * limit;
      let leadsUrl = `${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/leads?limit=${limit}&offset=${offset}&owner=true&fpPhones=true&status=not_contact_saved${leadStatusParam}&values=true&ai_data=true`;
      if (searchAddress) {
        leadsUrl += `&address=${encodeURIComponent(searchAddress)}`;
      }

      const resp = await axiosInstance.get(leadsUrl);
      const data = await resp.data;
      if (data.success) {
        const sortedLeads = (data.leads || []).sort((a, b) => (b.id || 0) - (a.id || 0));
        // setLeads(sortedLeads);
        // setTotalLeads(data.total);
        setCurrentPage(page);

        setLeads((prevLeads) => {
          const prevMap = Object.fromEntries(prevLeads.map((l) => [l.id, l]));
          return sortedLeads.map((newLead) => {
            const prev = prevMap[newLead.id];
            return {
              ...newLead,
              // Preserve existing marketData and loading flag if they exist
              marketData: prev?.marketData ?? newLead.marketData,
              marketDataLoading: prev?.marketDataLoading ?? false,
            };
          });
        });

        setTotalLeads(data.total);

        const addressList = sortedLeads.map((l) => l.full_address).filter(Boolean);
        const zipList = [...new Set(sortedLeads.map((l) => l.zip).filter(Boolean))];

        if (showLoader && addressList.length && zipList.length) {
          fetchMarketDataBatch(addressList, zipList, showLoader);
        }
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchMarketDataBatch = async (addressList = [], zipList = [], showLoader = true) => {
    if (!addressList?.length || !zipList?.length) return;

    // Only show loading indicators if explicitly requested
    if (showLoader) {
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: true } : lead)));
    }

    try {
      const response = await axiosInstance.post(`/api/webhooks/market-data-batch`, {
        full_address: addressList,
      });

      if (response.data?.success) {
        let marketDataMap = {};
        if (Array.isArray(response.data.data)) {
          marketDataMap = Object.fromEntries(response.data.data.map((item) => [item.zip, item]));
        }

        setLeads((prevLeads) =>
          prevLeads.map((lead) => {
            const marketData = marketDataMap[lead.zip];
            // Always update marketData and clear loading for that lead
            return {
              ...lead,
              marketData: marketData ? marketData.normalizedMarket : lead.marketData,
              marketDataLoading: false,
            };
          }),
        );
      } else {
        // On API failure, clear loading for all affected leads
        setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
      }
    } catch (error) {
      console.error("Failed to fetch market data", error);
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
    }
  };

  useEffect(() => {
    fetchLeads(currentPage, leadsPerPage, true);
    const interval = setInterval(() => {
      fetchLeads(currentPage, leadsPerPage, false);
    }, 10000);
    return () => clearInterval(interval); // eslint-disable-next-line
  }, [currentPage, searchAddress]);

  const handleAddressSearch = (address) => {
    setSearchAddress(address);
    setCurrentPage(1);
  };

  const handleClearAddressSearch = () => {
    setSearchAddress("");
    setCurrentPage(1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const handleOpenMoveModal = (row) => {
    setSelectedRow(row);
    setMoveModalOpen(true);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteLead = async (identifier) => {
    try {
      const res = await axiosInstance.put(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/dm-lead`, {
        lead_id: identifier,
        deleted: true,
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "❌ Failed to Delete lead");
      }

      toast.success("Lead Deleted successfully.");

      fetchLeads();
    } catch (err) {
      console.error("Error deleting lead:", err);
      toast.error(err?.response?.data?.message || err.message || "Error Deleting Lead.");
    }
  };

  const totalTableWidth = filterColumns.reduce((sum, col) => sum + col.width, 0);

  const [selectedData, setSelectedData] = useState({});

  const toggleOwner = (leadId, owner) => {
    setSelectedData((prev) => {
      const prevLeadId = Object.keys(prev)[0];

      // Selecting owner from another lead → clear all
      if (prevLeadId && prevLeadId !== String(leadId)) {
        return {
          [leadId]: {
            owners: {
              [owner.id]: { owner, phones: [], email: null },
            },
          },
        };
      }

      const currentOwners = prev[leadId]?.owners || {};

      // Toggle owner
      if (currentOwners[owner.id]) {
        const updatedOwners = { ...currentOwners };
        delete updatedOwners[owner.id];

        return Object.keys(updatedOwners).length ? { [leadId]: { owners: updatedOwners } } : {};
      }

      return {
        [leadId]: {
          owners: {
            ...currentOwners,
            [owner.id]: { owner, phones: [], email: null },
          },
        },
      };
    });
  };

  const togglePhone = (leadId, owner, phoneObj) => {
    setSelectedData((prev) => {
      const currentOwner = prev[leadId]?.owners?.[owner.id];
      if (!currentOwner) return prev;

      const exists = currentOwner.phones.some((p) => p.phone === phoneObj.phone);

      const phones = exists ? currentOwner.phones.filter((p) => p.phone !== phoneObj.phone) : [...currentOwner.phones, phoneObj];

      return {
        [leadId]: {
          owners: {
            ...prev[leadId].owners,
            [owner.id]: { ...currentOwner, phones },
          },
        },
      };
    });
  };

  const toggleEmail = (leadId, owner, email) => {
    setSelectedData((prev) => {
      const currentOwner = prev[leadId]?.owners?.[owner.id];
      if (!currentOwner) return prev;

      return {
        [leadId]: {
          owners: {
            ...prev[leadId].owners,
            [owner.id]: {
              ...currentOwner,
              email: currentOwner.email === email ? null : email,
            },
          },
        },
      };
    });
  };

  const confirmSelection = ({ lead }) => {
    const owners = Object.values(selectedData[lead.id].owners).map((o) => ({
      firstName: o.owner.first_name,
      lastName: o.owner.last_name,
      email: o.email,
      phones: o.phones.map((p) => `${p.phone}${p.type ? " " + getPhoneTypeLabel(p.type) : ""}`).join("\n"),
    }));

    const selectedLead = {
      address: lead.full_address,
      owners,
    };

    const params = new URLSearchParams({
      lead: JSON.stringify(selectedLead),
      page: "1",
    });

    window.open(`/added-leads?${params.toString()}`, "_blank");
  };
  const addAddress = ({ lead }) => {
    const selectedLead = {
      address: lead.full_address,
      owners: [
        {
          firstName: "",
          lastName: "",
          email: "",
          phones: "",
        },
      ],
    };

    const params = new URLSearchParams({
      lead: JSON.stringify(selectedLead),
      page: "1",
    });

    window.open(`/added-leads?${params.toString()}`, "_blank");
  };

  useEffect(() => {
    const storedVisited = JSON.parse(localStorage.getItem("visitedLinks") || "[]");
    const storedCopied = JSON.parse(localStorage.getItem("copiedAddresses") || "[]");
    setVisitedLinks(storedVisited);
    setCopiedAddresses(storedCopied);
  }, []);

  const mapLeadData = (lead) => ({
    id: lead.id || "Not Found",
    created_at: formatTime(lead.created_at),
    lead_submitted: formatTime(lead.lead_submitted),
    status: statuses[lead.status] || lead.status,
    market_score: lead.marketData?.market_score != null ? <span className={getMetricScoreClass(lead.marketData.market_score >= 71 ? 2 : lead.marketData.market_score >= 41 ? 1 : 0)}>{lead.marketData.market_score} / 100</span> : "",
    full_address:
      (
        <div className="flex justify-between !space-x-4">
          <div className="flex item-center">
            <span>{lead.full_address}</span>
            <span className="!ml-2 flex !space-x-2">
              <a
                href={makeFpsLink(lead.address, lead.zip, lead.city, lead.state)}
                target="_blank"
                rel="noopener noreferrer"
                className={`!ml-2 text-nowrap ${visitedLinks.includes(lead.id) ? "!text-purple-800 text-medium" : "!text-blue-600 hover:!text-blue-800"}`}
                onClick={() => {
                  const updated = updateLocalStorageRolling("visitedLinks", lead.id);
                  setVisitedLinks(updated);
                }}
              >
                [ FPS ]
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(lead.full_address ?? "");
                  setCopiedId(lead.id);

                  const updated = updateLocalStorageRolling("copiedAddresses", lead.id);
                  setCopiedAddresses(updated);

                  setTimeout(() => setCopiedId(null), 2000);
                }}
                title={lead.full_address}
                className={`flex justify-center items-center !w-6 !h-6 rounded-full cursor-pointer ${copiedAddresses.includes(lead.id) ? "bg-green-600 text-white" : "bg-gray-200/70 hover:bg-gray-200/100 text-black"}`}
              >
                {copiedId === lead.id ? <CopyCheck className="!w-4 !h-4 text-black" /> : <Copy className="!w-4 !h-4" />}
              </button>
            </span>
          </div>
          <div>
            <button
              type="button"
              onClick={() => {
                addAddress({ lead });
              }}
              className="!px-3 !py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer text-nowrap"
            >
              Add Address
            </button>
          </div>
        </div>
      ) || "Not Found",
    dm_lead_id: lead.dm_lead_id || "Not Found",
    values: (
      <div className="flex flex-wrap gap-2">
        {lead.values?.length > 0 ? (
          lead.values.map((v) =>
            v.url ? (
              <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline text-sm">
                [{v.site}]
              </a>
            ) : null,
          )
        ) : (
          <span className="text-gray-400 text-sm"></span>
        )}
      </div>
    ),
    owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) => (
          <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
            <span>
              {o.dm_owner ? "D" : "P"}-{o.name}
            </span>
          </div>
        ))}
      </div>
    ) : (
      "Not Found"
    ),
    dm_owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) =>
          o.dm_owner ? (
            <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
              <span>{o.name}</span>
            </div>
          ) : null,
        )}
      </div>
    ) : (
      "Not Found"
    ),
    p_owners: lead.owner?.length ? (
      <div>
        {lead.owner.map((o, idx) =>
          !o.dm_owner ? (
            <div key={o.id || idx} className="font-semibold" style={{ color: getOwnerColor(idx) }}>
              <span>{o.name}</span>
            </div>
          ) : null,
        )}
      </div>
    ) : (
      "Not Found"
    ),
    fpPhones: lead.owner?.length ? (
      <>
        {lead.owner.map((owner, idx) => {
          const ownerPhones = lead.fpPhones?.filter((p) => p.owner_id === owner.id).map((p) => p) || [];
          const ownerEmails = [owner.email, owner.email_2, owner.email_3].filter(Boolean);

          const ownerSelected = !!selectedData[lead.id]?.owners?.[owner.id];

          return (
            <div key={idx} className="!mb-2 transition-all duration-200 ease-in-out">
              <label className="font-semibold flex item-center cursor-pointer" style={{ color: getOwnerColor(idx) }}>
                <input type="checkbox" className="!w-4 !h-4 accent-teal-600 !mr-2 cursor-pointer" checked={ownerSelected} onChange={() => toggleOwner(lead.id, owner)} />

                <span>{owner.name || "Unknown Owner"}</span>
              </label>

              {ownerPhones.length > 0 && (
                <div className="text-gray-700">
                  {ownerPhones.map((phone, index) => (
                    <label key={index} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="!w-4 !h-4 accent-teal-600 cursor-pointer" checked={ownerSelected && selectedData[lead.id]?.owners?.[owner.id]?.phones?.some((p) => p.phone === phone.phone)} onChange={() => togglePhone(lead.id, owner, phone)} />
                      <span>
                        {formatUSPhone(phone.phone)}
                        {phone.type && (
                          <>
                            {" - "}
                            {getPhoneTypeLabel(phone.type)}
                          </>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {ownerEmails.length > 0 && (
                <div className="text-gray-500">
                  {ownerEmails.map((email, index) => (
                    <label key={index} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="!w-4 !h-4 accent-teal-600 cursor-pointer" checked={ownerSelected && selectedData[lead.id]?.owners?.[owner.id]?.email === email} onChange={() => toggleEmail(lead.id, owner, email)} />
                      <span>{email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {!!selectedData[lead.id]?.owners && (
          <div className="!pl-10">
            <button onClick={() => confirmSelection({ lead })} className="!px-3 !py-1 bg-teal-600 text-white text-sm ! rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer transition-all duration-200 ease-in-out">
              Add Lead
            </button>
          </div>
        )}
      </>
    ) : (
      "Not Found"
    ),
    error_message: lead.is_loading ? <div>Loading ...</div> : <div>{getErrorMessageText(lead.error_message)}</div>,
    reason_move: <div>{lead.reason_move}</div>,
    within_city_limits: lead?.ai_data?.within_city_limits !== null && lead?.ai_data?.within_city_limits !== undefined ? <span>{lead?.ai_data.within_city_limits ? "Yes" : "No"}</span> : "",
    metro_details:
      lead?.ai_data?.nearest_metro || lead?.ai_data?.metro_distance_miles || lead?.ai_data?.metro_distance_minutes || lead?.ai_data?.metro_population ? (
        <div>
          {lead?.ai_data?.nearest_metro && (
            <div>
              <strong>Metro:</strong> {lead?.ai_data.nearest_metro}
            </div>
          )}
          {lead?.ai_data?.metro_distance_miles != null && (
            <div>
              <strong>Distance:</strong> {lead?.ai_data.metro_distance_miles} mi
            </div>
          )}
          {lead?.ai_data?.metro_distance_minutes != null && (
            <div>
              <strong>Time:</strong> {lead?.ai_data.metro_distance_minutes} min
            </div>
          )}
          {lead?.ai_data?.metro_population != null && (
            <div>
              <strong>Population:</strong> {lead?.ai_data.metro_population.toLocaleString()}
            </div>
          )}
        </div>
      ) : (
        ""
      ),
    nearby_density: lead?.ai_data?.nearby_density != null ? <span>{lead?.ai_data.nearby_density.toLocaleString()}</span> : "",
    area_type: lead?.ai_data?.area_type ? <span style={{ textTransform: "capitalize" }}>{lead?.ai_data.area_type}</span> : "",
  });

  return (
    <>
      <div>
        <AddLeadForm onSearch={handleAddressSearch} onClear={handleClearAddressSearch} />

        <div className="mt-6 !w-full !overflow-x-auto">
          <table className="!border !border-gray-300 !table-auto min-w-[97%]" style={{ width: `${totalTableWidth}px` }}>
            <thead>
              <tr className="!bg-gray-200">
                {permission?.permission_name === "not-added-leads-l1" && <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>}
                {(permissionDelete?.permission_name === "not-added-leads-l2" || permissionDelete?.permission_name === "not-added-leads") && <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>}
                {filterColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-4 !py-2`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                      textAlign: col.headerAlign,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!fetchLoading ? (
                <>
                  {leads.length > 0 ? (
                    leads.map((lead, idx) => {
                      const row = mapLeadData(lead);
                      return (
                        <tr key={lead.id || idx} className="!hover:bg-gray-100 transition-all duration-200 ease-in-out">
                          {permission?.permission_name === "not-added-leads-l1" && (
                            <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                              <button onClick={() => handleOpenMoveModal(row)} className="!text-blue-600 hover:!text-blue-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Move Lead">
                                <ArrowBigRightDashIcon size={18} />
                              </button>
                            </td>
                          )}
                          {(permissionDelete?.permission_name === "not-added-leads-l2" || permissionDelete?.permission_name === "not-added-leads") && (
                            <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                              <button onClick={() => handleOpenDeleteModal(row)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Delete Lead">
                                <Trash2 size={18} />
                              </button>
                            </td>
                          )}
                          {filterColumns.map((col) => (
                            <td
                              key={col.key}
                              className={`truncate !border !border-gray-300 !px-4 !py-2 text-sm transition-all duration-200 ease-in-out
                            ${row[col.key] === "Not Found" ? "text-red-500" : /(CONFIRMED)/i.test((row[col.key] ?? "").toString()) ? "text-green-500" : /(NO-ANSWER)/i.test((row[col.key] ?? "").toString()) ? "text-yellow-500" : "text-black"}`}
                              style={{
                                minWidth: `${col.width}px`,
                                width: `${col.width}px`,
                                textAlign: col.cellAlign,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={filterColumns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={filterColumns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <Pagination totalItems={totalLeads} currentPage={currentPage} itemsPerPage={leadsPerPage} onPageChange={(page) => fetchLeads(page)} />

        <DMReasonConfirmModal isOpen={moveModalOpen} setMoveModalOpen={setMoveModalOpen} onCancel={() => setMoveModalOpen(false)} selectedRow={selectedRow} identifier={selectedRow?.id} fetchData={fetchLeads} />

        {/* Delete Confirm Modal */}
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete this lead?`}
          isOpen={deleteModalOpen}
          onConfirmTitle="Delete"
          onConfirm={() => {
            if (selectedRow) {
              handleDeleteLead(selectedRow.id, 1);
              // console.log("lead deleted", selectedRow.id, 1);
            }
            setDeleteModalOpen(false);
          }}
          onCancelTitle="Cancel"
          onCancel={() => setDeleteModalOpen(false)}
        />
      </div>
    </>
  );
};

export default NotAddedLeads;
